import { useState } from "react";
import classNames from "classnames";
import countryList from "@/data/countries.json";
import { ColoredLogoWithoutName, DownArrow } from "@/components/svg";
import Link from "next/link";
import { getTotalPackingCost } from "@/lib/calc-packing";
import { getRoundedWeight } from "@/lib/shipment-functions";
import {
  RURAL_SURCHARGE_SGD,
  DIRECT_SIGNATURE_CHARGE_SGD,
} from "@/lib/constants";
import { getShipmentChargeableWeight } from "@/lib/shipment-functions";

export default function ShipmentFormNavbar({ formData, packages, country }) {
  const [showContent, setShowContent] = useState(false);

  let countryName;

  if (country) {
    const countryInfo = countryList.find(
      (countryObj) => countryObj.isoCode === country
    );
    countryName = countryInfo.name;
  }

  const handleToggle = () => {
    const mainTab = document.getElementById(`shipment-summary`);
    const content = document.getElementById(`shipment-summary-content`);
    const bufferHeight = 100;
    if (showContent) {
      content.style.maxHeight = "0px";
    } else {
      content.style.maxHeight = `${
        mainTab.scrollHeight + content.scrollHeight + bufferHeight
      }px`;
    }
    setShowContent(!showContent);
  };
  const {
    initialFreightQuote,
    initialDiscountAmount,
    initialValentinesAmount,
    dutiesPaidBy,
    taxes,
    duties,
    dutyHandlingFee,
    insuranceAmount,
    isInsured,
    directSignature,
    isRural,
  } = formData;
  const totalAmount =
    parseFloat(initialFreightQuote) +
    (dutiesPaidBy == "SENDER"
      ? dutyHandlingFee + parseFloat(taxes) + parseFloat(duties)
      : 0) +
    getTotalPackingCost(packages) +
    (isInsured ? parseFloat(insuranceAmount) : 0) +
    (directSignature ? DIRECT_SIGNATURE_CHARGE_SGD : 0) +
    (isRural ? RURAL_SURCHARGE_SGD : 0) -
    initialDiscountAmount +
    initialValentinesAmount;

  const shipmentChargeableWeight = getShipmentChargeableWeight(formData);

  return (
    <div className="max-w-7xl mx-auto md:px-6 py-3">
      <div id={"shipment-summary"} className="scroll-mt-28">
        <div className="grid grid-cols-[1fr_4fr] px-2">
          <Link href="/dashboard/orders">
            <div className="cursor-pointer w-min">
              <ColoredLogoWithoutName className="w-12 md:w-16" />
            </div>
          </Link>

          <div
            id="form-navbar-toggle"
            className="flex justify-between items-center gap-x-2 md:gap-x-4 cursor-pointer"
            onClick={() => handleToggle()}
          >
            <div className="grid grid-cols-[3fr_1fr] w-full">
              <div className="hidden md:block place-self-center text-dark-purple font-bold text-xs md:text-xl whitespace-nowrap">
                Shipment to {countryName} - {packages.length} x Pkg
              </div>
              <div className="md:hidden place-self-center text-dark-purple font-bold text-xs md:text-xl whitespace-nowrap">
                Shipment to {country} - {packages.length} x Pkg
              </div>
              <div className="block w-full text-dark-purple font-bold text-sm md:text-xl whitespace-nowrap">
                <div className="float-right text-base md:text-2xl">
                  <p>
                    S${" "}
                    <span id="form-navbar-total-amount">
                      {totalAmount.toFixed(2)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <span>
              <DownArrow
                className={`w-[18px] h-[10px] transition-transform duration-300 ease-in-out ${
                  showContent ? "-rotate-180" : ""
                }`}
              />
            </span>
          </div>
        </div>

        <div
          id={`shipment-summary-content`}
          className={classNames("accordion-content overflow-hidden")}
        >
          <div className="pb-6 text-dark-purple font-bold text-xs md:text-base">
            <div className="flex flex-col gap-y-2">
              <hr className="border-dark-purple my-3"></hr>
              <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                <div>
                  Shipping - Chargeable Weight{" "}
                  {/* TODO: revisit this when launching cheaper shipping options on consumer shipment form */}
                  {getRoundedWeight(shipmentChargeableWeight).toFixed(1)}kg
                </div>
                <div>
                  $
                  <span id="form-navbar-shipping-charge">
                    {initialFreightQuote.toFixed(2)}
                  </span>
                </div>
              </div>
              {packages.map((parcel, index) => {
                const packingCost = getTotalPackingCost([parcel]);
                if (packingCost) {
                  return (
                    <div
                      className="flex justify-between px-8 md:pl-[25%] md:pr-11"
                      key={`packing-cost-p${index}`}
                    >
                      <div>Packing - Package {index + 1}</div>
                      <div>
                        $
                        <span id={`form-navbar-packing-charge-p${index}`}>
                          {packingCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                }
              })}
              {isInsured && (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                  <div>Insurance</div>
                  <div>
                    $
                    <span id="form-navbar-insurance-charge">
                      {insuranceAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {directSignature && (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                  <div>Direct Signature</div>
                  <div>
                    $
                    <span id="form-navbar-signature-charge">
                      {DIRECT_SIGNATURE_CHARGE_SGD.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {dutiesPaidBy === "SENDER" && (taxes || duties) ? (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                  <div>Taxes & Duties</div>
                  <div>
                    $
                    <span id="form-navbar-taxes-duties-charge">
                      {(taxes + duties + dutyHandlingFee).toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : null}
              {isRural && (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                  <div>Rural Surcharge</div>
                  <div>
                    $
                    <span id="form-navbar-rural-surcharge">
                      {RURAL_SURCHARGE_SGD.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {!!initialValentinesAmount && (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11">
                  <div>{"Spades and Shovels Mini Bouquet"}</div>
                  <div>
                    <span>${initialValentinesAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}
              {!!initialDiscountAmount && (
                <div className="flex justify-between px-8 md:pl-[25%] md:pr-11 text-pink">
                  <div>Promotional Discount</div>
                  <div>
                    <span>-${initialDiscountAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
